/* istanbul ignore file */
import {DateTime} from 'luxon';
import React, {createContext, useCallback, useContext, useMemo} from 'react';

import {Country} from '@br/idm-api';
import {CountryNames} from '@br/idm-components';

type CountrySelection = {label: string; value: Country};
export type IntlFormatter = {
    formatter: (
        options: Omit<
            Intl.DateTimeFormatOptions,
            'calendar' | 'numberingSystem' | 'timeZone' | 'formatMatcher' | 'localeMatcher'
        >
    ) => Intl.DateTimeFormat;
    localStringToJsDate: (dateString: string) => Date;
    formatDateToDateInputFormat: (date: Date, type: 'date' | 'datetime-local') => string;
    formatDate: (date: Date) => string;
    formatTime: (date: Date) => string;
    formatDateTime: (date: Date) => string;
    formatNumber: (number: number, options?: Intl.NumberFormatOptions) => string;
    formatCompactNumber: (
        number: number,
        options?: Omit<Intl.NumberFormatOptions, 'notation' | 'compactDisplay'>
    ) => string;
    formatList: (values: string[]) => string;
    rtl: boolean;
    timeZone: string;
    getCountryFromCode: (countryCode: string) => string;
    filterCountrySelection: (filter: string) => CountrySelection[];
};

const IntlFormatterContext = createContext<IntlFormatter>({} as IntlFormatter);
export const useIntlFormatter = (): IntlFormatter => useContext(IntlFormatterContext);

export const IntlFormatProvider = ({
    children,
    locale,
    timeZone,
    rtl,
}: React.PropsWithChildren<{
    locale: string;
    timeZone: string;
    rtl: boolean;
}>): JSX.Element => {
    const formatter: IntlFormatter['formatter'] = useCallback(
        format => {
            return Intl.DateTimeFormat([locale], {timeZone, ...format});
        },
        [locale, timeZone]
    );

    /**
     * Function accepts date string in the format YYYY-MM-DD or YYYY-MM-DDTHH:MM
     * and converts that timestring to a Javascript date object parsing the date as the configured timezone
     */
    const localStringToJsDate = useCallback(
        (dateString: string) => {
            const type = dateString.length === 10 ? 'date' : 'datetime-local';
            const parsingFormat = type === 'date' ? 'yyyy-MM-dd' : 'yyyy-MM-dd HH:mm';

            const d = DateTime.fromFormat(dateString.replace('T', ' '), parsingFormat, {zone: timeZone});
            return d.toJSDate();
        },
        [timeZone]
    );

    /**
     * Converts a javascript date object to the format needed in use with the native
     * <input type="date" /> or <input type="datetime-local" />
     * returning the value as YYYY-MM-DD or YYYY-MM-DDTHH:MM based on `type` arg.
     * in the users configured timezone ignoring the browser timezone settings.
     */
    const formatDateToDateInputFormat = useCallback(
        (date: Date, type: 'date' | 'datetime-local') => {
            const inputFormat = type === 'date' ? 'yyyy-MM-dd' : 'yyyy-MM-dd HH:mm';
            return DateTime.fromJSDate(date, {zone: timeZone}).toFormat(inputFormat).replace(' ', 'T');
        },
        [timeZone]
    );
    const formatDate = useCallback(
        (date: Date) => Intl.DateTimeFormat([locale], {timeZone, dateStyle: 'medium'}).format(date),
        [locale, timeZone]
    );
    const formatTime = useCallback(
        (date: Date) => Intl.DateTimeFormat([locale], {timeZone, timeStyle: 'medium'}).format(date),
        [locale, timeZone]
    );
    const formatDateTime = useCallback(
        (date: Date) =>
            Intl.DateTimeFormat([locale], {timeZone, dateStyle: 'medium', timeStyle: 'medium'}).format(date),
        [locale, timeZone]
    );
    const formatNumber = useCallback<IntlFormatter['formatNumber']>(
        (number, options) => Intl.NumberFormat([locale], options).format(number),
        [locale]
    );
    const formatCompactNumber = useCallback<IntlFormatter['formatCompactNumber']>(
        (number, options) =>
            Intl.NumberFormat([locale], {notation: 'compact', compactDisplay: 'short', ...options}).format(number),
        [locale]
    );
    const formatList = useCallback(
        (values: string[]) => new Intl.ListFormat([locale], {style: 'long', type: 'unit'}).format(values),
        [locale]
    );

    const getCountryFromCode = useCallback(
        (countryCode: string) =>
            new Intl.DisplayNames([locale], {type: 'region'}).of(countryCode) || CountryNames[countryCode as Country],
        [locale]
    );
    const countrySelection = useMemo<CountrySelection[]>(() => {
        const names = new Intl.DisplayNames([locale], {type: 'region'});
        return Object.entries(CountryNames)
            .map(([value, label]) => ({value: value as Country, label: names.of(value) || label}))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [locale]);
    const filterCountrySelection = useCallback(
        (filter: string) => {
            const labelFilter = filter.toLocaleLowerCase();
            return countrySelection.filter(value => {
                const label = value.label.toLocaleLowerCase();
                return label.startsWith(labelFilter) || label.includes(' ' + labelFilter);
            });
        },
        [countrySelection]
    );

    return (
        <IntlFormatterContext.Provider
            value={{
                formatter,
                formatDate,
                formatTime,
                formatDateTime,
                localStringToJsDate,
                formatDateToDateInputFormat,
                formatNumber,
                formatCompactNumber,
                formatList,
                rtl,
                timeZone,
                getCountryFromCode,
                filterCountrySelection,
            }}
        >
            {children}
        </IntlFormatterContext.Provider>
    );
};

export * from './global';
export * from './gen/enums';
